import type { SubmissionAuditEvent, SubmissionDetail } from '../../types/shared';

interface Props {
  events:         SubmissionAuditEvent[];
  /** Optional approval row, woven into the timeline at the right point so the
   *  rendered view matches the intent: "submitted ... edited ... approved." */
  periodApproval: SubmissionDetail['periodApproval'];
}

interface TimelineRow {
  id:         string;
  changedAt:  string;
  actorName:  string;
  actorRole:  string;
  description: string;
}

function describeEvent(e: SubmissionAuditEvent): string {
  switch (e.eventType) {
    case 'created': {
      const fc = e.fieldChanges as { source?: string; is_empty?: boolean; backfilled?: boolean };
      if (fc.backfilled) return `Submission imported from Google Form (backfilled)`;
      if (fc.is_empty)   return `Submitted as "nothing to submit"`;
      return `Submission created${fc.source ? ` via ${fc.source}` : ''}`;
    }
    case 'line_item_changed': {
      const fc = e.fieldChanges as {
        items?: {
          added?:   Array<{ item_id: string; quantity: number }>;
          updated?: Array<{ item_id: string; before: number; after: number }>;
          removed?: Array<{ item_id: string; quantity: number }>;
        };
      };
      const parts: string[] = [];
      const a = fc.items?.added?.length   ?? 0;
      const u = fc.items?.updated?.length ?? 0;
      const r = fc.items?.removed?.length ?? 0;
      if (a > 0) parts.push(`added ${a} item${a === 1 ? '' : 's'}`);
      if (u > 0) parts.push(`updated ${u} item${u === 1 ? '' : 's'}`);
      if (r > 0) parts.push(`removed ${r} item${r === 1 ? '' : 's'}`);
      return parts.length === 0 ? 'Edited line items' : `Edited line items (${parts.join(', ')})`;
    }
    case 'marked_empty':   return 'Marked as "nothing to submit"';
    case 'unmarked_empty': return 'Unmarked "nothing to submit"';
    case 'period_changed': {
      const fc = e.fieldChanges as { period_month?: { before: string; after: string } };
      return `Moved from ${fc.period_month?.before ?? '?'} to ${fc.period_month?.after ?? '?'}`;
    }
    case 'notes_updated':  return 'Updated notes';
    default:               return e.eventType;
  }
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString([], {
    year:  'numeric',
    month: 'short',
    day:   '2-digit',
    hour:  '2-digit',
    minute: '2-digit',
  });
}

export default function AuditLogTimeline({ events, periodApproval }: Props) {
  // Compose a unified timeline by merging submission events + (optional)
  // approval/reopen events from the (region, period) approval row. Sort by
  // timestamp ascending.
  const rows: TimelineRow[] = events.map(e => ({
    id:          e.id,
    changedAt:   e.changedAt,
    actorName:   e.actorName,
    actorRole:   e.actorRole,
    description: describeEvent(e),
  }));

  if (periodApproval?.approvedAt && periodApproval.approvedBy) {
    rows.push({
      id:          'approval',
      changedAt:   periodApproval.approvedAt,
      actorName:   periodApproval.approvedBy.name,
      actorRole:   'admin/rd',
      description: `Approved the close period${periodApproval.reopenedAt ? ' (later re-opened)' : ''}`,
    });
  }
  if (periodApproval?.reopenedAt && periodApproval.reopenedBy) {
    rows.push({
      id:          'reopen',
      changedAt:   periodApproval.reopenedAt,
      actorName:   periodApproval.reopenedBy.name,
      actorRole:   'admin',
      description: `Re-opened the close period${periodApproval.reopenReason ? ` — ${periodApproval.reopenReason}` : ''}`,
    });
  }

  rows.sort((a, b) => a.changedAt.localeCompare(b.changedAt));

  if (rows.length === 0) {
    return (
      <div className="text-sm text-stone-400 dark:text-stone-500 py-2">
        No audit events recorded yet.
      </div>
    );
  }

  return (
    <ol className="space-y-3 border-l border-stone-200 dark:border-stone-700 pl-4">
      {rows.map(row => (
        <li key={row.id} className="relative">
          <span className="absolute -left-[21px] top-1.5 w-3 h-3 rounded-full bg-moss-500" />
          <div className="text-sm text-stone-800 dark:text-stone-200">
            {row.description}
          </div>
          <div className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
            {row.actorName}
            <span className="text-stone-400 dark:text-stone-500"> · </span>
            <span className="font-mono">{row.actorRole}</span>
            <span className="text-stone-400 dark:text-stone-500"> · </span>
            {formatDateTime(row.changedAt)}
          </div>
        </li>
      ))}
    </ol>
  );
}
